/** Мини-легенда сноса поверх карты. Видна только когда снос включён и есть треки.
 *  Оборачивается в CollapsibleLegend (закладка ↔ всплывающее тело), поэтому тело
 *  интерактивно (pointer-events-auto от обёртки) — своего pointer-events-none нет. */
import { useT } from '../i18n'
import { DRIFT_SOURCE_COLOR } from './driftMarkerStyle'
import { gradeLegendScale } from './driftLegendScale'

/**
 * Настройка кабинета (заказ владельца 2026-08-13): фактический вылов на карте
 * гасится двумя признаками — точки и ореол погрешности. Скрытое уносит свою
 * строку: подпись к тому, чего в кадре нет, — враньё. `anonymized` = «точки
 * скрыты, места огрублены» (одно состояние); строка-объяснение про огрубление
 * остаётся — она отвечает на «почему круги без точек». Отметки косяков (ручные)
 * настройкой не закрываются, их строки остаются всегда.
 */
export function DriftLegend({
  anonymized = false,
  catchHaloHidden = false,
}: {
  anonymized?: boolean
  catchHaloHidden?: boolean
}) {
  // Геометрия ряда ступеней — из общей с картой таблицы радиусов.
  const gradeScale = gradeLegendScale()
  const t = useT()
  return (
    <div className="min-w-[196px] rounded-xl border border-hull bg-abyss/85 px-3.5 py-3 shadow-console backdrop-blur">
      <div className="mb-2 text-xs font-semibold text-foam">{t('models.drift.legend.title')}</div>

      {/* Наблюдение (полая точка) → трек с затуханием → текущее положение (залитая). */}
      <div className="flex items-center gap-2 text-[11px] text-mist">
        <svg viewBox="0 0 64 12" className="h-3 w-16 shrink-0" aria-hidden>
          <circle cx="5" cy="6" r="3.2" fill="none" stroke="#0ea5e9" strokeWidth="1.4" />
          <line x1="9" y1="6" x2="52" y2="6" stroke="#0ea5e9" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="58" cy="6" r="3.6" fill="#0ea5e9" />
        </svg>
        <span>{t('models.drift.legend.observedToNow')}</span>
      </div>
      <div className="mt-0.5 pl-[4.75rem] text-[10px] text-faint">
        {t('models.drift.legend.fadeIsAge')}
      </div>

      {/* Словарь разведён: НАЧЕРТАНИЕ = качество данных, ЯРКОСТЬ = акцент перемотки,
          МАРКЕР = статус трека. */}
      <div className="mt-2 space-y-1 text-[11px] text-mist">
        {/* Отметка происхождения — общая для косяка и факта вылова. Без строки
            в легенде бледный кружок читается как «старый косяк». */}
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 24 12" className="h-3 w-6 shrink-0" aria-hidden>
            <circle cx="6" cy="6" r="2.4" fill="#0ea5e9" opacity="0.3" />
            <line x1="9" y1="6" x2="20" y2="6" stroke="#0ea5e9" strokeWidth="1.2"
              opacity="0.5" strokeLinecap="round" />
          </svg>
          <span>{t('models.drift.legend.origin')}</span>
        </div>
        {/* Маркеры двух дат — появляются только при перемотке. */}
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 24 12" className="h-3 w-6 shrink-0" aria-hidden>
            <circle cx="6" cy="6" r="2.4" fill="#0ea5e9" opacity="0.4" />
            <circle cx="18" cy="6" r="4" fill="#0ea5e9" />
          </svg>
          <span>{t('models.drift.legend.todayToDay')}</span>
        </div>
        {/* Факт — сплошная (посчитано по фактическим течениям). */}
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 24 12" className="h-3 w-6 shrink-0" aria-hidden>
            <line x1="1" y1="6" x2="23" y2="6" stroke="#0ea5e9" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <span>{t('models.drift.legend.fact')}</span>
        </div>
        {/* Прогноз — пунктир (прогнозные течения), независимо от ползунка. */}
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 24 12" className="h-3 w-6 shrink-0" aria-hidden>
            <line
              x1="1"
              y1="6"
              x2="23"
              y2="6"
              stroke="#0ea5e9"
              strokeWidth="1.6"
              strokeDasharray="3 3"
              strokeLinecap="butt"
            />
          </svg>
          <span>{t('models.drift.legend.forecast')}</span>
        </div>
        {/* Бледная тонкая — остальной трек вне выбранного отрезка. */}
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 24 12" className="h-3 w-6 shrink-0" aria-hidden>
            <line
              x1="1"
              y1="6"
              x2="23"
              y2="6"
              stroke="#0ea5e9"
              strokeWidth="0.9"
              opacity="0.4"
              strokeLinecap="round"
            />
          </svg>
          <span>{t('models.drift.legend.restOfPath')}</span>
        </div>
        {/* Полый маркер — трек замёрз: на выбранный день данных нет. */}
        <div className="flex items-center gap-2">
          <span
            className="ml-[7px] inline-block h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: 'transparent', boxShadow: '0 0 0 1.8px #f59e0b' }}
          />
          <span>{t('models.drift.legend.lastKnown')}</span>
        </div>
        {/* beached — красная обводка, уткнулся в берег. */}
        <div className="flex items-center gap-2">
          <span
            className="ml-[7px] inline-block h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: '#0ea5e9', boxShadow: '0 0 0 1.5px #ef4444' }}
          />
          <span>{t('models.drift.legend.beached')}</span>
        </div>
        {/* Ореол неопределённости — шире с днями сноса и на прогнозных течениях.
            Скрыт настройкой кабинета — строки нет: объяснять нечего. */}
        {!catchHaloHidden && (
          <>
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 12" className="h-3 w-6 shrink-0" aria-hidden>
                <circle cx="12" cy="6" r="5" fill="#38bdf8" fillOpacity="0.12"
                  stroke="#38bdf8" strokeOpacity="0.5" strokeWidth="1" />
                <circle cx="12" cy="6" r="1.8" fill="#0ea5e9" />
              </svg>
              <span>{t('models.drift.legend.halo')}</span>
            </div>
            <div className="pl-[1.9rem] text-[10px] text-faint">
              {t('models.drift.legend.haloHint')}
            </div>
          </>
        )}
      </div>

      {/* Ступени размера ФАКТА ВЫЛОВА — потому и янтарные, цветом вылова: у
          косяка размер задаёт его класс, оценённый наблюдателем, а не доля от
          эталона. Радиусы берём из общей с картой таблицы (gradeLegendScale):
          легенда — образец маркера, а не его иллюстрация. Маркеры вылова скрыты
          настройкой — ступеней размера в кадре нет, и строки тоже. */}
      {!anonymized && (
        <>
          <div className="mt-2 flex items-center gap-2 border-t border-hull/60 pt-1.5 text-[11px] text-mist">
            <svg
              viewBox={`0 0 ${gradeScale.width} ${gradeScale.height}`}
              width={gradeScale.width}
              height={gradeScale.height}
              className="shrink-0"
              aria-hidden
            >
              {gradeScale.circles.map(c => (
                <circle key={c.r} cx={c.cx} cy={c.cy} r={c.r} fill={DRIFT_SOURCE_COLOR.catch} />
              ))}
            </svg>
            <span>{t('models.drift.legend.catchSizes')}</span>
          </div>
          <div className="text-[10px] text-faint" style={{ paddingLeft: gradeScale.width + 8 }}>
            {t('models.drift.legend.catchSizesHint')}
          </div>
        </>
      )}

      {/* Источник точки: косяк / вылов. */}
      <div className="mt-2 flex items-center gap-3 border-t border-hull/60 pt-1.5 text-[11px] text-mist">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#0ea5e9' }} />
          {t('models.drift.legend.school')}
        </span>
        {!anonymized && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#e0a54b' }} />
            {t('models.drift.legend.catch')}
          </span>
        )}
      </div>

      {/* Точек вылова в кадре нет вовсе — без этой строки пустые ореолы читаются
          как поломка данных, а не как настройка кабинета. */}
      {anonymized && !catchHaloHidden && (
        <div className="mt-2 border-t border-hull/60 pt-1.5 text-[10px] leading-snug text-faint">
          {t('models.drift.legend.anonymizedLong')}
        </div>
      )}
    </div>
  )
}
